import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Lock, Pencil, Send, Undo2, X } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { Button } from '@/components/Button';
import { MonthPicker } from '@/components/MonthPicker';
import { Modal } from '@/components/Modal';
import { EmptyState } from '@/components/EmptyState';
import { DateLabel } from '@/components/DateLabel';
import { StatusBadge } from '@/components/WorkRecordModal';
import {
  WorkRecordAmendForm, emptyAmendDraft, buildAmendmentPayload,
  type WorkRecordAmendDraft, type WorkRecordAmendmentPayload,
} from '@/components/WorkRecordAmendForm';
import type { ReasonCode } from '@/components/MonthlyEntryDaySheet';
import { isoToLocalHm } from '@/lib/shift-format';
import { flattenNodes } from '@/lib/node-tree';
import type { NodeTreeItem, NodesResponse } from '@/stores/scope';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';

/**
 * `/shift/work-record-approvals` (v0.4後半 月次実績一括入力 第2フェーズ I-5b、§7-5)。
 * 設計: docs/design/v04-monthly-attendance-bulk-entry-phase2-detail.md §7-5
 *
 * ⚠️ 追加セッション (2026-09-27) で「修正して承認」「reopen(差し戻し)」「承認後修正」のUIを追加し、
 * 同日中の2回目の追加セッションで computeSalaryDecreaseDirection のlate/early副チェック活性化・
 * 修正フォームへの区分/遅刻早退理由編集・部分承認(承認待ちの複数日選択)を追加した。
 * 同日中の3回目の追加セッション(§13-29)で以下3点を追加:
 * 1. 所属外店舗(応援)勤務の行に店舗名バッジを表示(§7-5、`home_node_id!==work_node_id`で判定。
 *    GET /work-records (serializeWorkRecord) が元々返している home_node_id/work_node_id の2列を
 *    使うだけなのでAPI変更は無し。design-and-code MEDIUM是正: 具体的な行番号は今後ずれるため書かない)。
 * 2. reopen(差し戻し)の確認ダイアログに「有休の残り日数がN日戻ります」の実測値を表示
 *    (新設の読み取り専用API `POST /work-records/monthly-reopen/estimate`)。
 * 3. reopen(差し戻し)にも部分承認と同じ複数日選択UIを追加(承認待ちの複数日選択は§13-28で
 *    既に実装済みだったため、今回はまだ1日ずつだったreopenのみを拡張した)。
 * 同日中の4回目の追加セッション(§13-29残課題②)で、承認後修正(approver_edit)にも複数日まとめ編集を
 * 追加した。単一postEditDraftを`amendDrafts`(修正して承認)と同型のMapへ一般化し、個別行の「修正する」
 * は下書きを貯めるだけ(完了/破棄)、実際の試算・提出はページ側の「◯件を修正する」ボタンが
 * まとめて行う。バックエンドは元々amendments配列(最大62件)を受け付ける設計だったため、
 * フロントのUI変更のみで対応できた。
 *
 * 意図的に絞った範囲 (残課題、次回セッションで要否を相談):
 * 1. 修正フォーム (WorkRecordAmendForm) は休憩の内訳の編集は不可 (理由は同コンポーネントの
 *    コメント参照、時刻を送らなければサーバー側が既存内訳を再検証する)。
 */

type ApprovalQueuePerson = {
  employee_id: string | null;
  external_person_id: string | null;
  display_name: string | null;
  approvable_count: number;
  approved_count: number;
  warnings: {
    minor_labor_violation: boolean;
    min_break_shortfall_days: number;
    outside_home_node_work_days: number;
    proxy_entry_days: number;
  };
  on_time_as_planned_days: number;
  // §7-5「予定どおり 18/22日」表示用(分母)。
  worked_days: number;
  day_summary: {
    determinable: boolean;
    public_holiday_days: number;
    paid_leave_days: number;
    compensatory_off_days: number;
    absence_days: number;
    holiday_work_days: number;
    total_labor_minutes: number;
    night_minutes: number;
    overtime_minutes: number | null;
  } | null;
};
type QueueSettings = { amend_on_approve: boolean; post_approval_edit_mode: 'locked' | 'reopen' | 'approver_edit' };
type ApprovalQueueResponse = { period: string; settings: QueueSettings; people: ApprovalQueuePerson[] };

type WorkRecord = {
  work_record_id: string; work_date: string; start_at: string; end_at: string; break_minutes: number;
  record_kind: 'worked' | 'absence'; status: 'draft' | 'submitted' | 'approved' | 'rejected';
  reason_code_label: string | null; reason_code_id: string | null; deviation_reason_code_id: string | null;
  work_node_id: string; home_node_id: string; row_version: number;
};
type ReopenEstimateResponse = {
  restorable: Array<{ leave_type_id: string; leave_type_label: string; days: number }>;
  total_days: number; target_count: number; authorized_count: number;
};
type ReasonCodesResponse = { reason_codes: ReasonCode[] };

type SalaryComparison = {
  total_labor_minutes: number; scheduled_within_minutes: number; non_statutory_overtime_minutes: number;
  statutory_overtime_minutes: number; over_monthly_60h_minutes: number; night_minutes: number;
  legal_holiday_work_minutes: number; paid_leave_days: number;
  late_count: number; late_minutes: number; early_leave_count: number; early_leave_minutes: number; absence_days: number;
};
type DryRunResponse = {
  blocked: boolean; block_reason: 'reduces_pay' | 'reason_changed_to_leave' | null;
  before: SalaryComparison | null; after: SalaryComparison | null;
};

const today = () => new Date().toISOString().slice(0, 10);

const personKeyOf = (p: { employee_id: string | null; external_person_id: string | null }): string =>
  p.employee_id ? `E:${p.employee_id}` : `X:${p.external_person_id}`;

/** ①法令違反 > ②所属外店舗の勤務 > ③代理入力 (§7-5 の優先順位)。 */
const primaryWarning = (w: ApprovalQueuePerson['warnings']): { key: 'legal' | 'outside' | 'proxy'; label: string } | null => {
  if (w.minor_labor_violation || w.min_break_shortfall_days > 0) return { key: 'legal', label: '法令違反の疑い' };
  if (w.outside_home_node_work_days > 0) return { key: 'outside', label: `所属外店舗の勤務 ${w.outside_home_node_work_days}日` };
  if (w.proxy_entry_days > 0) return { key: 'proxy', label: `代理入力 ${w.proxy_entry_days}日` };
  return null;
};
const warningCount = (w: ApprovalQueuePerson['warnings']): number =>
  (w.minor_labor_violation || w.min_break_shortfall_days > 0 ? 1 : 0) + (w.outside_home_node_work_days > 0 ? 1 : 0) + (w.proxy_entry_days > 0 ? 1 : 0);

/** 767px以下=カード積み / 768〜1279px=テーブル+右Modal / 1280px以上=テーブル+右パネル常設 (§7-5)。 */
const useLayoutTier = (): 'stack' | 'tablet' | 'desktop' => {
  const [tier, setTier] = useState<'stack' | 'tablet' | 'desktop'>(() => {
    if (typeof window === 'undefined') return 'desktop';
    const w = window.innerWidth;
    return w <= 767 ? 'stack' : w <= 1279 ? 'tablet' : 'desktop';
  });
  useEffect(() => {
    const onResize = () => {
      const w = window.innerWidth;
      setTier(w <= 767 ? 'stack' : w <= 1279 ? 'tablet' : 'desktop');
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return tier;
};

function WarningBadge({ warning }: { warning: { key: 'legal' | 'outside' | 'proxy'; label: string } }) {
  const cls = warning.key === 'legal'
    ? 'text-danger-text bg-danger-bg'
    : 'text-text-sub bg-surface-sunken';
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs shrink-0 ${cls}`}>
      <AlertTriangle size={12} className="shrink-0" />
      <span className="truncate max-w-[10rem]">{warning.label}</span>
    </span>
  );
}

function NeutralBadge({ children }: { children: React.ReactNode }) {
  return <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs shrink-0 text-text-sub bg-surface-sunken">{children}</span>;
}

/** §7-5「lockedの会社は鍵アイコンと理由の文言」。法令違反ではないため中立色(§6-2)。
 *  文言はバックエンドの403(WORK_RECORD_POST_APPROVAL_EDIT_LOCKED)と同じにして一貫させる。 */
function LockedEditNotice() {
  return (
    <NeutralBadge>
      {/* design-expert Minor是正: 他のバッジ(WarningBadge・所属外店舗バッジ)と同じ長文防御
          パターンに揃える(CJKは任意位置で折り返りうるため)。 */}
      <span className="inline-flex items-center gap-1 whitespace-nowrap"><Lock size={11} className="shrink-0" />この会社では変更できない設定です</span>
    </NeutralBadge>
  );
}

/** 承認者が一覧性で区分別日数・時間をチェックできるようにする要約行(user要望、2026-09-28)。
 *  「公休が4日必要なのに2日しかない」等の異常に承認者が気付きやすくする目的のため、0件の区分も
 *  隠さずそのまま表示する(既定の勤務パターン等との照合はここでは行わない、承認者の目視判断に委ねる)。 */
function DaySummaryLine({ summary }: { summary: ApprovalQueuePerson['day_summary'] }) {
  if (!summary) return null;
  return (
    <div className="text-text-sub tabular-nums text-xs flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
      <span>公休 {summary.public_holiday_days}日</span>
      <span>有休 {summary.paid_leave_days}日</span>
      <span>振休 {summary.compensatory_off_days}日</span>
      <span>欠勤 {summary.absence_days}日</span>
      <span>休日出勤 {summary.holiday_work_days}日</span>
      <span>総労働 {formatSalaryValue(summary.total_labor_minutes, 'hours')}</span>
      <span>深夜 {formatSalaryValue(summary.night_minutes, 'hours')}</span>
      {summary.determinable ? (
        <span>残業 {formatSalaryValue(summary.overtime_minutes ?? 0, 'hours')}</span>
      ) : (
        <span className="flex items-center gap-1 text-alert-text px-1.5 py-0.5 rounded bg-alert-bg shrink-0">
          <AlertTriangle size={11} className="shrink-0" />残業時間は算出できません
        </span>
      )}
    </div>
  );
}

const SALARY_FIELDS: Array<{ key: keyof SalaryComparison; label: string; unit: 'hours' | 'days' | 'count' }> = [
  { key: 'total_labor_minutes', label: '総労働', unit: 'hours' },
  { key: 'scheduled_within_minutes', label: '所定内', unit: 'hours' },
  { key: 'non_statutory_overtime_minutes', label: '所定外', unit: 'hours' },
  { key: 'statutory_overtime_minutes', label: '法定外', unit: 'hours' },
  { key: 'over_monthly_60h_minutes', label: 'うち月60h超', unit: 'hours' },
  { key: 'night_minutes', label: '深夜', unit: 'hours' },
  { key: 'legal_holiday_work_minutes', label: '法定休日労働', unit: 'hours' },
  { key: 'paid_leave_days', label: '有休', unit: 'days' },
  { key: 'late_count', label: '遅刻回数', unit: 'count' },
  { key: 'late_minutes', label: '遅刻時間', unit: 'hours' },
  { key: 'early_leave_count', label: '早退回数', unit: 'count' },
  { key: 'early_leave_minutes', label: '早退時間', unit: 'hours' },
  { key: 'absence_days', label: '欠勤', unit: 'days' },
];
const formatSalaryValue = (v: number, unit: 'hours' | 'days' | 'count'): string => (
  unit === 'hours' ? `${(v / 60).toFixed(1)}h` : unit === 'days' ? `${v}日` : `${v}件`
);

function SalaryDiffTable({ before, after }: { before: SalaryComparison; after: SalaryComparison }) {
  const rows = SALARY_FIELDS.filter((f) => before[f.key] !== after[f.key]);
  if (rows.length === 0) return <p className="text-xs text-text-sub">給料に影響する項目に変化はありません。</p>;
  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="text-left text-text-sub border-b border-surface-border">
          <th className="font-normal py-1">項目</th><th className="font-normal py-1">変更前</th><th className="font-normal py-1">変更後</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((f) => (
          <tr key={f.key} className="border-b border-surface-border">
            <td className="py-1">{f.label}</td>
            <td className="py-1 tabular-nums">{formatSalaryValue(before[f.key], f.unit)}</td>
            <td className="py-1 tabular-nums font-medium">{formatSalaryValue(after[f.key], f.unit)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function BlockedReasonMessage({ reason }: { reason: 'reduces_pay' | 'reason_changed_to_leave' }) {
  return (
    <p className="text-sm text-danger-text">
      {reason === 'reduces_pay'
        ? 'この修正は本人の給料が減る方向です。差し戻して本人に直してもらってください。'
        : '区分を休暇に変える修正はできません。差し戻して本人に選び直してもらってください。'}
    </p>
  );
}

/** 1日分の記録カード (承認待ち/確定済みとも表示、状態に応じて修正・差し戻すボタンを出す)。
 *  MonthlyEntryDaySheet を流用しない理由は WorkRecordAmendForm 冒頭のコメント参照。 */
function DayRecordsPanel({
  date, records, amendMode, amendDrafts, editingId, onStartEdit, onChangeDraft, onDoneEdit,
  postApprovalEditMode, onReopen, postEditingId, postEditDrafts, onStartPostEdit, onChangePostEditDraft, onCancelPostEdit, onDonePostEdit, postEditActionsDisabled,
  reasonCodes, deviationReasonCodes, nodeNameById,
}: {
  date: string; records: WorkRecord[];
  amendMode: boolean; amendDrafts: Map<string, WorkRecordAmendDraft>; editingId: string | null;
  onStartEdit: (rec: WorkRecord) => void; onChangeDraft: (id: string, draft: WorkRecordAmendDraft) => void; onDoneEdit: () => void;
  postApprovalEditMode: QueueSettings['post_approval_edit_mode'];
  onReopen: (rec: WorkRecord) => void;
  // 追加セッション(2026-09-27、承認後修正の複数日まとめ編集): 単一postEditDraftをMapへ一般化し、
  // 「修正して承認」(amendDrafts)と同型のパターンにする。個別行では下書きの保存(完了)/破棄のみ行い、
  // 実際の試算・提出はページ側のまとめて修正ボタン(全postEditDraftsを一括送信)が担う。
  postEditingId: string | null; postEditDrafts: Map<string, WorkRecordAmendDraft>;
  onStartPostEdit: (rec: WorkRecord) => void; onChangePostEditDraft: (id: string, draft: WorkRecordAmendDraft) => void;
  onCancelPostEdit: (id: string) => void; onDonePostEdit: () => void;
  // design-expert Minor是正: まとめて修正の試算・送信中は新たに編集を始められないようにする
  // (decideMut.isPending 中に「差し戻し」を disabled にする既存パターンとの整合)。
  postEditActionsDisabled: boolean;
  reasonCodes: ReasonCode[]; deviationReasonCodes: ReasonCode[]; nodeNameById: Map<string, string>;
}) {
  return (
    <div className="space-y-2">
      <p className="text-sm font-bold"><DateLabel date={date} withWeekday /></p>
      {records.length === 0 ? <p className="text-sm text-text-sub">この日の記録はありません。</p> : records.map((r) => (
        <div key={r.work_record_id} className="border border-surface-border rounded-lg p-3 space-y-2 bg-surface-sunken/40">
          <div className="flex items-center gap-2 flex-wrap">
            <StatusBadge status={r.status} /><span className="text-xs text-text-sub">{r.reason_code_label ?? '通常勤務'}</span>
            {/* §7-5: 所属外店舗(応援)勤務の行は店舗名つきで薄く表示する (§6-2の色ルール、中立の情報チップ)。 */}
            {r.work_node_id !== r.home_node_id && (
              // design-expert Major是正: 隣接するWarningBadge (truncate max-w-[10rem]) と同じパターンに揃える
              // (長い店舗名で1行チップが複数行に折り返るのを防ぐ)。
              <NeutralBadge><span className="truncate max-w-[8rem] inline-block align-bottom">{nodeNameById.get(r.work_node_id) ?? r.work_node_id}</span></NeutralBadge>
            )}
          </div>
          {r.record_kind === 'worked' && (
            editingId === r.work_record_id ? (
              <div className="space-y-2">
                <WorkRecordAmendForm
                  value={amendDrafts.get(r.work_record_id) ?? emptyAmendDraft(r.start_at, r.end_at, r.reason_code_id, r.deviation_reason_code_id)}
                  onChange={(d) => onChangeDraft(r.work_record_id, d)}
                  reasonCodes={reasonCodes} deviationReasonCodes={deviationReasonCodes}
                />
                <Button size="sm" variant="secondary" onClick={onDoneEdit}>完了</Button>
              </div>
            ) : postEditingId === r.work_record_id ? (
              <div className="space-y-2">
                <WorkRecordAmendForm
                  value={postEditDrafts.get(r.work_record_id) ?? emptyAmendDraft(r.start_at, r.end_at, r.reason_code_id, r.deviation_reason_code_id)}
                  onChange={(d) => onChangePostEditDraft(r.work_record_id, d)}
                  reasonCodes={reasonCodes} deviationReasonCodes={deviationReasonCodes}
                />
                <div className="flex gap-1.5">
                  <Button size="sm" variant="secondary" onClick={onDonePostEdit}>完了</Button>
                  {/* design-expert Major是正: 「完了」と見た目が同じだと取消不能な下書き喪失を誤タップしうる。
                      dangerは同画面の主要CTA(承認)と重複するため使わず、アイコンで区別する。 */}
                  <Button size="sm" variant="secondary" leftIcon={<X size={12} />} onClick={() => onCancelPostEdit(r.work_record_id)}>破棄</Button>
                </div>
              </div>
            ) : (
              <>
                <p className="text-xs text-text-sub tabular-nums">{isoToLocalHm(r.start_at)}〜{isoToLocalHm(r.end_at)}（休憩{r.break_minutes}分）</p>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {amendMode && r.status === 'submitted' && (
                    <Button size="sm" variant="secondary" leftIcon={<Pencil size={12} />} onClick={() => onStartEdit(r)}>
                      {amendDrafts.has(r.work_record_id) ? '編集済み・修正する' : '修正する'}
                    </Button>
                  )}
                  {r.status === 'approved' && postApprovalEditMode !== 'locked' && (
                    <>
                      <Button size="sm" variant="secondary" leftIcon={<Undo2 size={12} />} onClick={() => onReopen(r)}>差し戻す</Button>
                      {postApprovalEditMode === 'approver_edit' && (
                        <Button size="sm" variant="secondary" leftIcon={<Pencil size={12} />} disabled={postEditActionsDisabled} onClick={() => onStartPostEdit(r)}>
                          {postEditDrafts.has(r.work_record_id) ? '編集済み・修正する' : '修正する'}
                        </Button>
                      )}
                    </>
                  )}
                  {/* §7-5「lockedの会社は鍵アイコンと理由の文言」。ボタンが単に消えるだけだと、
                      承認者が「なぜ差し戻す/修正するボタンが無いのか」を画面上判別できなかった。 */}
                  {r.status === 'approved' && postApprovalEditMode === 'locked' && <LockedEditNotice />}
                </div>
              </>
            )
          )}
          {r.record_kind === 'absence' && r.status === 'approved' && postApprovalEditMode !== 'locked' && (
            <Button size="sm" variant="secondary" leftIcon={<Undo2 size={12} />} onClick={() => onReopen(r)}>差し戻す</Button>
          )}
          {r.record_kind === 'absence' && r.status === 'approved' && postApprovalEditMode === 'locked' && <LockedEditNotice />}
        </div>
      ))}
    </div>
  );
}

/** 人×期間の月の明細。承認待ち(submitted)の一括承認/差し戻し/修正して承認と、確定済み(approved)の
 *  差し戻す(reopen)/修正する(承認後修正)の両方をこのパネルの中で扱う (§7-5)。 */
function PersonPeriodDetail({ person, period, settings, onDecided, nodeNameById }: {
  person: ApprovalQueuePerson; period: string; settings: QueueSettings; onDecided: () => void; nodeNameById: Map<string, string>;
}) {
  // design-expert CRITICAL是正 (追加セッション2回目): 320/375px の bottom-sheet (実効幅約288px) に
  // 日付チェックボックス追加後、2カラム(日付一覧|日別詳細)のままだと日付文字列がカラム境界を
  // 越えて衝突する。stack tier(767px以下)のみ1カラム(縦積み)に切り替える。tablet(右ドロワー
  // 480px幅)・desktop(28remサイドバー)は実効幅に余裕があるため2カラムのまま。
  const detailLayoutTier = useLayoutTier();
  const [from, to] = useMemo(() => {
    const [y, m] = period.split('-').map(Number);
    const last = new Date(Date.UTC(y!, m!, 0)).getUTCDate();
    return [`${period}-01`, `${period}-${String(last).padStart(2, '0')}`];
  }, [period]);
  const dates = useMemo(() => {
    const out: string[] = [];
    let cursor = from;
    while (cursor <= to) { out.push(cursor); const d = new Date(`${cursor}T00:00:00Z`); d.setUTCDate(d.getUTCDate() + 1); cursor = d.toISOString().slice(0, 10); }
    return out;
  }, [from, to]);

  const query = useQuery<{ work_records: WorkRecord[] }>({
    queryKey: ['work-record-approvals-detail', person.employee_id, person.external_person_id, period],
    queryFn: () => apiFetch<{ work_records: WorkRecord[] }>(
      `/work-records?month=${period}${person.employee_id ? `&employee_id=${person.employee_id}` : `&external_person_id=${person.external_person_id}`}`,
    ),
  });
  const { data: reasonCodesData } = useQuery<ReasonCodesResponse>({
    queryKey: ['attendance-reason-codes', 'work-record-approvals'],
    queryFn: () => apiFetch<ReasonCodesResponse>('/masters/attendance-reason-codes'),
  });
  const allCodes = reasonCodesData?.reason_codes ?? [];
  const reasonCodes = useMemo(() => allCodes.filter((r) => r.category === 'absence' || r.category === 'holiday_work').sort((a, b) => a.sort_order - b.sort_order), [allCodes]);
  const deviationReasonCodes = useMemo(() => allCodes.filter((r) => r.category === 'late' || r.category === 'early_leave').sort((a, b) => a.sort_order - b.sort_order), [allCodes]);
  const byDate = useMemo(() => {
    const map = new Map<string, WorkRecord[]>();
    for (const r of query.data?.work_records ?? []) map.set(r.work_date, [...(map.get(r.work_date) ?? []), r]);
    return map;
  }, [query.data]);
  const [openDate, setOpenDate] = useState<string | null>(null);
  useEffect(() => { setOpenDate(dates.find((d) => (byDate.get(d) ?? []).length > 0) ?? null); }, [dates, byDate]);

  const qc = useQueryClient();
  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ['work-record-approvals-detail'] });
    onDecided();
  };

  // ---- 承認待ち: 差し戻し・(修正して)承認 ----
  const [reasonOpen, setReasonOpen] = useState(false);
  const [comment, setComment] = useState('');
  const [amendMode, setAmendMode] = useState(false);
  const [amendDrafts, setAmendDrafts] = useState<Map<string, WorkRecordAmendDraft>>(new Map());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [confirmDryRun, setConfirmDryRun] = useState<{ amendments: WorkRecordAmendmentPayload[]; result: DryRunResponse } | null>(null);
  const [dryRunPending, setDryRunPending] = useState(false);
  // 追加セッション(2026-09-27、部分承認UI): 個別日を選んで承認・差し戻しの対象を絞る (§7-3の
  // target_work_record_ids をUIから使う)。未選択(空集合)なら従来どおり全ての承認待ち日が対象。
  const [selectedDates, setSelectedDates] = useState<Set<string>>(new Set());
  const submittedRecordIdsForDate = (date: string): string[] =>
    (byDate.get(date) ?? []).filter((r) => r.status === 'submitted').map((r) => r.work_record_id);
  const targetWorkRecordIds = useMemo(() => (
    selectedDates.size === 0 ? undefined : [...selectedDates].flatMap(submittedRecordIdsForDate)
  ), [selectedDates, byDate]);
  // QA LOW是正: 「修正する」でその日を暗黙選択した後、ユーザーが手動でチェックを外した場合、
  // その日の修正下書きはサーバー側で黙って対象外(continue)になり反映されない (§7-3の
  // target_work_record_ids フィルタ後の authorizedIds チェック)。チェックを外した時点で
  // その日の下書きも一緒に破棄し、「対象から外す=編集内容も取り消す」を一貫させる。
  const toggleDateSelection = (date: string) => {
    setSelectedDates((prev) => {
      const next = new Set(prev);
      if (next.has(date)) {
        next.delete(date);
        const droppedIds = new Set(submittedRecordIdsForDate(date));
        setAmendDrafts((drafts) => {
          if (![...drafts.keys()].some((id) => droppedIds.has(id))) return drafts;
          const nextDrafts = new Map(drafts);
          for (const id of droppedIds) nextDrafts.delete(id);
          return nextDrafts;
        });
        setEditingId((id) => (id && droppedIds.has(id) ? null : id));
      } else {
        next.add(date);
      }
      return next;
    });
  };

  // design-expert HIGH是正: トグルOFF時に下書き・編集中状態を必ず捨てる (「通常の承認に戻す」と
  // ラベルで主張しながら amendDrafts/editingId が残ると、その後の「承認」がラベルに反して
  // 修正内容付きで実行されてしまう)。
  const toggleAmendMode = () => {
    setAmendMode((v) => {
      const next = !v;
      if (!next) { setAmendDrafts(new Map()); setEditingId(null); }
      return next;
    });
  };

  const decideMut = useMutation({
    mutationFn: (input: { decision: 'approve' | 'reject'; amendments?: WorkRecordAmendmentPayload[] }) => apiFetch('/work-records/monthly-decide', {
      method: 'POST',
      body: JSON.stringify({
        employee_id: person.employee_id, external_person_id: person.external_person_id, period, decision: input.decision,
        ...(input.decision === 'reject' ? { comment } : {}),
        ...(input.amendments && input.amendments.length > 0 ? { amendments: input.amendments } : {}),
        ...(targetWorkRecordIds ? { target_work_record_ids: targetWorkRecordIds } : {}),
      }),
    }),
    onSuccess: (res: unknown) => {
      const decided = (res as { decided?: string[] })?.decided?.length ?? 0;
      const skipped = (res as { skipped?: unknown[] })?.skipped?.length ?? 0;
      toast.success(`${decided}件を反映しました${skipped > 0 ? `（${skipped}件は対象外でした）` : ''}`);
      setReasonOpen(false); setComment(''); setAmendMode(false); setAmendDrafts(new Map()); setEditingId(null); setConfirmDryRun(null);
      setSelectedDates(new Set());
      invalidateAll();
    },
    onError: (e) => {
      toast.error(e instanceof ApiClientError ? e.message : '反映に失敗しました');
      // QA Major是正: ROW_VERSION_MISMATCH (楽観ロック競合) はモーダルを開いたまま再試行しても
      // 同じエラーを繰り返すだけなので、確認モーダルを閉じて最新データの再取得を促す。
      if (e instanceof ApiClientError && e.code === 'ROW_VERSION_MISMATCH') { setConfirmDryRun(null); invalidateAll(); }
    },
  });

  const buildPendingAmendments = (): WorkRecordAmendmentPayload[] => {
    const out: WorkRecordAmendmentPayload[] = [];
    for (const [workRecordId, draft] of amendDrafts) {
      const rec = (query.data?.work_records ?? []).find((r) => r.work_record_id === workRecordId);
      if (!rec) continue;
      const payload = buildAmendmentPayload(workRecordId, rec.row_version, rec.work_date, draft);
      if (payload) out.push(payload);
    }
    return out;
  };

  const runDryRunThenConfirm = async () => {
    const amendments = buildPendingAmendments();
    if (amendments.length === 0) { decideMut.mutate({ decision: 'approve' }); return; }
    setDryRunPending(true);
    try {
      const result = await apiFetch<DryRunResponse>('/work-records/monthly-decide/dry-run', {
        method: 'POST',
        body: JSON.stringify({ employee_id: person.employee_id, external_person_id: person.external_person_id, period, amendments }),
      });
      setConfirmDryRun({ amendments, result });
    } catch (e) {
      toast.error(e instanceof ApiClientError ? e.message : '試算に失敗しました');
    } finally {
      setDryRunPending(false);
    }
  };

  // ---- 確定済み: 差し戻す(reopen) ----
  // 追加セッション(2026-09-27、§13-29): reopen にも部分承認(§13-28)と同じ複数日選択を追加する。
  // 個別行の「差し戻す」ボタン([rec]の1件配列)と、まとめて差し戻すボタン(選択した全approved行の
  // 配列)の両方をこの1つのstateで受ける(単数/複数を型で分けない、バックエンドは元々配列前提)。
  const [selectedApprovedDates, setSelectedApprovedDates] = useState<Set<string>>(new Set());
  const approvedRecordsForDate = (date: string): WorkRecord[] => (byDate.get(date) ?? []).filter((r) => r.status === 'approved');
  const toggleApprovedDateSelection = (date: string) => {
    setSelectedApprovedDates((prev) => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date); else next.add(date);
      return next;
    });
  };
  // QA MEDIUM是正: 他の操作(invalidateAll等)でデータが変わり、選択済みの日がapproved行を
  // 失った場合にSetへ古いエントリが残ると、後日同じ日に別のapproved行が現れた際「選んでいないのに
  // 選択済み」になる(§13-28型の再発)。byDateが変わるたびに、現存しないapproved日を選択から外す。
  useEffect(() => {
    setSelectedApprovedDates((prev) => {
      const next = new Set([...prev].filter((d) => approvedRecordsForDate(d).length > 0));
      return next.size === prev.size ? prev : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- byDate の変化だけを見る (関数自体はbyDate由来)
  }, [byDate]);
  const [reopenTargets, setReopenTargets] = useState<WorkRecord[] | null>(null);
  const [reopenComment, setReopenComment] = useState('');
  const [reopenEstimate, setReopenEstimate] = useState<ReopenEstimateResponse | null>(null);
  const [reopenEstimatePending, setReopenEstimatePending] = useState(false);
  const openReopenModal = async (targets: WorkRecord[]) => {
    // QA MEDIUM是正: 選択済みの日が(上記の競合等で)approved行を失った直後にクリックされると
    // targetsが空配列になりうる。空のまま開くと「undefinedの承認済み実績を差し戻します」という
    // 壊れた文言が一瞬見えるため、何もしない。
    if (targets.length === 0) return;
    setReopenTargets(targets);
    setReopenEstimate(null);
    setReopenEstimatePending(true);
    try {
      const result = await apiFetch<ReopenEstimateResponse>('/work-records/monthly-reopen/estimate', {
        method: 'POST',
        body: JSON.stringify({
          employee_id: person.employee_id, external_person_id: person.external_person_id, period,
          target_work_record_ids: targets.map((r) => r.work_record_id),
        }),
      });
      setReopenEstimate(result);
    } catch {
      // 試算はあくまで案内用。失敗しても差し戻し自体の実行は妨げない(汎用の注意文にフォールバック)。
    } finally {
      setReopenEstimatePending(false);
    }
  };
  const closeReopenModal = () => { setReopenTargets(null); setReopenComment(''); setReopenEstimate(null); };
  const reopenMut = useMutation({
    mutationFn: () => apiFetch('/work-records/monthly-reopen', {
      method: 'POST',
      body: JSON.stringify({
        employee_id: person.employee_id, external_person_id: person.external_person_id, period,
        target_work_record_ids: reopenTargets!.map((r) => r.work_record_id), comment: reopenComment,
      }),
    }),
    onSuccess: () => { toast.success('差し戻しました'); closeReopenModal(); setSelectedApprovedDates(new Set()); invalidateAll(); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '差し戻しに失敗しました'),
  });

  // ---- 確定済み: 修正する(承認後修正) ----
  // 追加セッション(2026-09-27、§13-29残課題②): 複数日まとめ編集を追加する。「修正して承認」
  // (amendDrafts、承認待ち側)と同型に、単一postEditDraftをMap化した。個別行の「修正する」は
  // 下書きをMapへ足すだけで即時送信しない(「完了」で下書きを保持したまま閉じる、「破棄」で
  // そのdraftだけ削除)。実際の試算・提出は下記のまとめて修正ボタン(postEditDrafts全件を一括送信)
  // が担う。バックエンドの POST /monthly-post-approval-edit は元々 amendments 配列(最大62件)を
  // 受け付ける設計だったため、フロントのUI変更のみで対応できる。
  const [postEditingId, setPostEditingId] = useState<string | null>(null);
  const [postEditDrafts, setPostEditDrafts] = useState<Map<string, WorkRecordAmendDraft>>(new Map());
  const [postEditConfirm, setPostEditConfirm] = useState<{ amendments: WorkRecordAmendmentPayload[]; result: DryRunResponse } | null>(null);
  const [postEditDryRunPending, setPostEditDryRunPending] = useState(false);
  // QA型の是正(§13-29の教訓を踏襲): 他の操作(reopen等)でデータが変わり、下書き中の行が
  // approved でなくなった場合にMapへ古いエントリが残ると、まとめて修正の送信対象に含まれて
  // しまい monthly-post-approval-edit 側の全件一致チェックで無関係な行まで失敗させる。
  // byDate(query.dataから導出、selectedApprovedDates用の同型useEffectと同じ依存)が変わるたびに、
  // 現存しないapproved行の下書きを削除する。
  useEffect(() => {
    const allRecords = [...byDate.values()].flat();
    setPostEditDrafts((prev) => {
      if (prev.size === 0) return prev;
      let changed = false;
      const next = new Map(prev);
      for (const id of prev.keys()) {
        const rec = allRecords.find((r) => r.work_record_id === id);
        if (!rec || rec.status !== 'approved') { next.delete(id); changed = true; }
      }
      return changed ? next : prev;
    });
    setPostEditingId((cur) => (cur && !allRecords.some((r) => r.work_record_id === cur && r.status === 'approved') ? null : cur));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- byDate の変化だけを見る
  }, [byDate]);
  const postEditMut = useMutation({
    mutationFn: (amendments: WorkRecordAmendmentPayload[]) => apiFetch('/work-records/monthly-post-approval-edit', {
      method: 'POST',
      body: JSON.stringify({ employee_id: person.employee_id, external_person_id: person.external_person_id, period, amendments }),
    }),
    // QA Major是正: monthly-post-approval-editは行ごとにCONFLICTをskipへ積む部分成功設計
    // (§4-7と同じ規約)であり、決してthrowで全滅するわけではない。decideMutと同じく
    // amended/skippedの件数を読み、対象外があったことを利用者に伝える(黙って欠落させない)。
    onSuccess: (res: unknown) => {
      const amended = (res as { amended?: string[] })?.amended?.length ?? 0;
      const skipped = (res as { skipped?: unknown[] })?.skipped?.length ?? 0;
      toast.success(`${amended}件を修正しました${skipped > 0 ? `（${skipped}件は対象外でした）` : ''}`);
      setPostEditingId(null); setPostEditDrafts(new Map()); setPostEditConfirm(null); invalidateAll();
    },
    onError: (e) => {
      toast.error(e instanceof ApiClientError ? e.message : '修正に失敗しました');
      if (e instanceof ApiClientError && e.code === 'ROW_VERSION_MISMATCH') { setPostEditConfirm(null); invalidateAll(); }
    },
  });

  const buildPendingPostEditAmendments = (): WorkRecordAmendmentPayload[] => {
    const out: WorkRecordAmendmentPayload[] = [];
    for (const [workRecordId, draft] of postEditDrafts) {
      const rec = (query.data?.work_records ?? []).find((r) => r.work_record_id === workRecordId);
      if (!rec) continue;
      const payload = buildAmendmentPayload(workRecordId, rec.row_version, rec.work_date, draft);
      if (payload) out.push(payload);
    }
    return out;
  };

  const runPostEditDryRun = async () => {
    const amendments = buildPendingPostEditAmendments();
    if (amendments.length === 0) { toast.error('時刻を入力してください'); return; }
    setPostEditDryRunPending(true);
    try {
      const result = await apiFetch<DryRunResponse>('/work-records/monthly-decide/dry-run', {
        method: 'POST',
        body: JSON.stringify({ employee_id: person.employee_id, external_person_id: person.external_person_id, period, amendments }),
      });
      setPostEditConfirm({ amendments, result });
    } catch (e) {
      toast.error(e instanceof ApiClientError ? e.message : '試算に失敗しました');
    } finally {
      setPostEditDryRunPending(false);
    }
  };

  return (
    <div className="space-y-4">
      {person.approvable_count > 0 ? (
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div>
            <p className="font-semibold text-text-body">{person.display_name ?? '(氏名不明)'}</p>
            <p className="text-xs text-text-sub">承認待ち {person.approvable_count} 日分</p>
            {/* design-expert Minor是正: 氏名・件数に近接させる(近接の原則、ボタン群の後ろに置くと
                承認アクションと無関係な情報が挟まり視線導線が乱れる)。 */}
            {person.worked_days > 0 && (
              <NeutralBadge>予定どおり {person.on_time_as_planned_days}/{person.worked_days}日</NeutralBadge>
            )}
            <DaySummaryLine summary={person.day_summary} />
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <Button variant="secondary" size="sm" leftIcon={<Undo2 size={13} />} onClick={() => setReasonOpen(true)} disabled={decideMut.isPending}>
              差し戻し
            </Button>
            {settings.amend_on_approve && (
              <Button variant="secondary" size="sm" leftIcon={<Pencil size={13} />} onClick={toggleAmendMode} disabled={decideMut.isPending}>
                {amendMode ? '通常の承認に戻す' : '修正して承認'}
              </Button>
            )}
            <Button variant="primary" size="sm" leftIcon={<Send size={13} />} loading={decideMut.isPending || dryRunPending} onClick={() => void runDryRunThenConfirm()}>
              {amendDrafts.size > 0
                ? `${amendDrafts.size}件を修正して承認`
                : selectedDates.size > 0 ? `選択した${selectedDates.size}日を承認` : `${person.approvable_count}日分を承認`}
            </Button>
          </div>
        </div>
      ) : (
        <div>
          <p className="font-semibold text-text-body">{person.display_name ?? '(氏名不明)'}</p>
          <p className="text-xs text-text-sub">確定済み {person.approved_count} 日分</p>
          {/* §7-5「予定どおり 18/22日」(月の明細で表示、一覧のカードには出さない設計、§7-5参照)。
              中立色の情報チップ(§6-2、法令違反ではないためdanger.*は使わない)。 */}
          {person.worked_days > 0 && (
            <NeutralBadge>予定どおり {person.on_time_as_planned_days}/{person.worked_days}日</NeutralBadge>
          )}
          <DaySummaryLine summary={person.day_summary} />
        </div>
      )}
      {amendMode && <p className="text-xs text-text-sub">修正したい日を選び、記録の「修正する」から時刻を直してください。</p>}
      {person.approvable_count > 0 && (
        <p className="text-xs text-text-sub">
          {selectedDates.size > 0 ? '左の一覧でチェックした日だけが承認・差し戻しの対象です。' : '日を選ばず承認・差し戻しすると、承認待ちの全ての日が対象になります。'}
        </p>
      )}
      {settings.post_approval_edit_mode !== 'locked' && selectedApprovedDates.size > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          {/* design-expert Major是正: ラベル単独では320px幅でボタンと分離改行しうるため、
              既存の「選択した◯日を承認」(524行目)と同じく件数をボタン文言自体に埋め込み自己完結させる。 */}
          <Button
            variant="secondary" size="sm" leftIcon={<Undo2 size={13} />}
            onClick={() => void openReopenModal([...selectedApprovedDates].flatMap((d) => approvedRecordsForDate(d)))}
          >
            選択した{selectedApprovedDates.size}日をまとめて差し戻す
          </Button>
        </div>
      )}
      {/* 追加セッション(2026-09-27、§13-29残課題②): 承認後修正(approver_edit)の複数日まとめ編集。
          個別行の「修正する」で貯めた下書き(postEditDrafts)をまとめて試算・提出する。
          日付選択チェックボックスは不要(どの行を直すかはpostEditDraftsの内容そのものが表す)。 */}
      {postEditDrafts.size > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="secondary" size="sm" leftIcon={<Pencil size={13} />}
            loading={postEditDryRunPending || postEditMut.isPending}
            onClick={() => void runPostEditDryRun()}
          >
            {postEditDrafts.size}件を修正する
          </Button>
        </div>
      )}

      <div className={`grid gap-3 ${detailLayoutTier === 'stack' ? 'grid-cols-1' : 'grid-cols-[minmax(0,1fr)_minmax(0,1fr)]'}`}>
        <div className="space-y-1 max-h-[28rem] overflow-y-auto">
          {dates.map((date) => {
            const records = byDate.get(date) ?? [];
            if (records.length === 0) return null;
            const hasSubmitted = records.some((r) => r.status === 'submitted');
            const hasApproved = settings.post_approval_edit_mode !== 'locked' && records.some((r) => r.status === 'approved');
            // design-expert Major是正: まとめて修正の対象(postEditDrafts)は不可視の内部状態のため、
            // 日を開き直さないとどの日が編集キュー中か分からなかった。一覧に小さな目印を出す。
            const hasPendingPostEdit = records.some((r) => postEditDrafts.has(r.work_record_id));
            // user要望 (2026-09-28): 承認画面の一覧が「勤務」としか出ず、日を1つずつ開かないと
            // 何時から何時の勤務か分からず確認が面倒だった。MonthlyAttendanceEntryPage.tsx の
            // workDetail と同じパターン (店舗名+時刻を専用の行として追加) をこちらにも入れる。
            // 2店舗勤務等の複数行は要素が増えるほど縦に伸びるため、1件のみのときだけ表示する。
            const workDetail = (records.length === 1 && records[0]!.record_kind === 'worked')
              ? `${nodeNameById.get(records[0]!.work_node_id) ?? records[0]!.work_node_id} ${isoToLocalHm(records[0]!.start_at)}〜${isoToLocalHm(records[0]!.end_at)}`
              : null;
            return (
              // design-expert Major是正(部分対応): 同日に承認待ち・確定済み両方のチェックボックスが
              // 並ぶ稀なケース(応援等で1日2記録・状態が異なる場合)で区分ラベルの表示幅が圧迫されるため、
              // 行内gapを詰めて確保できる幅を増やす(44pxのタップ領域自体は変更しない)。
              // ⚠️ 残課題: 2チェックボックス同時表示時のレイアウト再設計は見送り(次回相談、稀なケースのため)。
              <div key={date} className="flex items-center gap-1">
                {hasSubmitted && (
                  <label className="min-h-[44px] min-w-[44px] flex items-center justify-center shrink-0">
                    <input type="checkbox" aria-label={`${date}を承認待ちとして選択`} checked={selectedDates.has(date)} onChange={() => toggleDateSelection(date)} />
                  </label>
                )}
                {hasApproved && (
                  <label className="min-h-[44px] min-w-[44px] flex items-center justify-center shrink-0">
                    <input type="checkbox" aria-label={`${date}を差し戻し対象として選択`} checked={selectedApprovedDates.has(date)} onChange={() => toggleApprovedDateSelection(date)} />
                  </label>
                )}
                <button
                  type="button" onClick={() => setOpenDate(date)}
                  className={`flex-1 min-w-0 flex flex-col justify-center gap-0.5 px-1.5 py-1.5 rounded text-left text-sm border min-h-[44px] ${
                    openDate === date ? 'border-misespark-navy bg-surface-sunken' : 'border-transparent hover:bg-surface-sunken'
                  }`}
                >
                  <span className="flex items-center justify-between gap-1 w-full min-w-0">
                    <span className="shrink-0 flex items-center gap-1">
                      <DateLabel date={date} withWeekday />
                      {hasPendingPostEdit && <Pencil size={11} className="text-text-sub shrink-0" aria-label="修正の下書きあり" />}
                    </span>
                    <span className="text-xs text-text-sub truncate min-w-0">{records.length === 2 ? '2店' : (records[0]?.reason_code_label ?? '勤務')}</span>
                  </span>
                  {workDetail && <span className="text-[11px] text-text-sub tabular-nums block">{workDetail}</span>}
                </button>
              </div>
            );
          })}
        </div>
        <div className="border border-surface-border rounded-lg p-3">
          {openDate ? (
            <DayRecordsPanel
              date={openDate}
              records={byDate.get(openDate) ?? []}
              amendMode={amendMode}
              amendDrafts={amendDrafts}
              editingId={editingId}
              onStartEdit={(rec) => {
                setEditingId(rec.work_record_id);
                setAmendDrafts((m) => (m.has(rec.work_record_id) ? m : new Map(m).set(rec.work_record_id, emptyAmendDraft(rec.start_at, rec.end_at, rec.reason_code_id, rec.deviation_reason_code_id))));
                // 部分承認UI: 修正する日を選ぶと、その日を暗黙に対象へ含める(選び忘れで
                // 修正が無視される事故を防ぐ、§7-3のtarget_work_record_idsと同期させる)。
                setSelectedDates((prev) => (prev.has(rec.work_date) ? prev : new Set(prev).add(rec.work_date)));
              }}
              onChangeDraft={(id, draft) => setAmendDrafts((m) => new Map(m).set(id, draft))}
              onDoneEdit={() => setEditingId(null)}
              postApprovalEditMode={settings.post_approval_edit_mode}
              onReopen={(rec) => void openReopenModal([rec])}
              postEditingId={postEditingId}
              postEditDrafts={postEditDrafts}
              onStartPostEdit={(rec) => {
                setPostEditingId(rec.work_record_id);
                setPostEditDrafts((m) => (m.has(rec.work_record_id) ? m : new Map(m).set(rec.work_record_id, emptyAmendDraft(rec.start_at, rec.end_at, rec.reason_code_id, rec.deviation_reason_code_id))));
              }}
              onChangePostEditDraft={(id, draft) => setPostEditDrafts((m) => new Map(m).set(id, draft))}
              onCancelPostEdit={(id) => {
                setPostEditDrafts((m) => { if (!m.has(id)) return m; const next = new Map(m); next.delete(id); return next; });
                setPostEditingId((cur) => (cur === id ? null : cur));
              }}
              onDonePostEdit={() => setPostEditingId(null)}
              postEditActionsDisabled={postEditDryRunPending || postEditMut.isPending}
              reasonCodes={reasonCodes} deviationReasonCodes={deviationReasonCodes} nodeNameById={nodeNameById}
            />
          ) : <p className="text-sm text-text-sub">日付を選んでください。</p>}
        </div>
      </div>

      <Modal open={reasonOpen} title="差し戻す理由" onClose={() => setReasonOpen(false)} size="sm"
        footer={(
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setReasonOpen(false)}>キャンセル</Button>
            <Button variant="danger" disabled={!comment.trim()} loading={decideMut.isPending} onClick={() => decideMut.mutate({ decision: 'reject' })}>差し戻す</Button>
          </div>
        )}
      >
        {selectedDates.size > 0 && <p className="text-xs text-text-sub mb-2">選択した{selectedDates.size}日のみが対象です。</p>}
        <label className="block text-xs text-text-sub mb-1">理由 (本人へ通知されます)</label>
        <textarea
          className="w-full min-h-[6rem] rounded border border-surface-border px-2.5 py-2 text-sm focus-ring"
          value={comment} onChange={(e) => setComment(e.target.value)} maxLength={500}
        />
      </Modal>

      <Modal open={!!confirmDryRun} title="修正内容の確認" onClose={() => setConfirmDryRun(null)} size="sm"
        footer={confirmDryRun && !confirmDryRun.result.blocked ? (
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setConfirmDryRun(null)}>キャンセル</Button>
            <Button variant="primary" loading={decideMut.isPending} onClick={() => decideMut.mutate({ decision: 'approve', amendments: confirmDryRun.amendments })}>この内容で承認</Button>
          </div>
        ) : (
          <div className="flex justify-end gap-2"><Button variant="secondary" onClick={() => setConfirmDryRun(null)}>閉じる</Button></div>
        )}
      >
        {confirmDryRun && (confirmDryRun.result.blocked ? (
          <BlockedReasonMessage reason={confirmDryRun.result.block_reason ?? 'reduces_pay'} />
        ) : (
          <div className="space-y-2">
            <p className="text-xs text-text-sub">本人に通知されます。</p>
            {confirmDryRun.result.before && confirmDryRun.result.after && <SalaryDiffTable before={confirmDryRun.result.before} after={confirmDryRun.result.after} />}
          </div>
        ))}
      </Modal>

      <Modal open={!!reopenTargets} title="差し戻す" onClose={closeReopenModal} size="sm"
        footer={(
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={closeReopenModal}>キャンセル</Button>
            <Button variant="danger" disabled={!reopenComment.trim()} loading={reopenMut.isPending} onClick={() => reopenMut.mutate()}>差し戻す</Button>
          </div>
        )}
      >
        <p className="text-xs text-text-sub mb-2">
          {reopenTargets && reopenTargets.length > 1
            ? `選択した${reopenTargets.length}件の承認済み実績を差し戻します。`
            : `${reopenTargets?.[0]?.work_date} の承認済みの実績を差し戻します。`}
        </p>
        {/* 追加セッション(2026-09-27、§13-29): §7-5「有休の残り日数が N 日戻ります」の実測値表示。
            試算APIが失敗しても差し戻し自体は続行できるよう、その場合は汎用の注意文にフォールバックする。 */}
        {reopenEstimatePending ? (
          <p className="text-xs text-text-sub mb-2">有休等の消化状況を確認しています…</p>
        ) : reopenEstimate ? (
          reopenEstimate.total_days > 0 ? (
            <p className="text-xs text-text-sub mb-2">
              差し戻すと、{reopenEstimate.restorable.map((r) => `${r.leave_type_label}が${r.days}日`).join('・')}分、本人の残数へ自動的に戻ります。
            </p>
          ) : (
            <p className="text-xs text-text-sub mb-2">有休等の消化はありません。</p>
          )
        ) : (
          <p className="text-xs text-text-sub mb-2">有休等が消化されている場合は自動的に元に戻ります。</p>
        )}
        <label className="block text-xs text-text-sub mb-1">理由 (本人へ通知されます)</label>
        <textarea
          className="w-full min-h-[6rem] rounded border border-surface-border px-2.5 py-2 text-sm focus-ring"
          value={reopenComment} onChange={(e) => setReopenComment(e.target.value)} maxLength={500}
        />
      </Modal>

      <Modal open={!!postEditConfirm} title="修正内容の確認" onClose={() => setPostEditConfirm(null)} size="sm"
        footer={postEditConfirm && !postEditConfirm.result.blocked ? (
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setPostEditConfirm(null)}>キャンセル</Button>
            <Button variant="primary" loading={postEditMut.isPending} onClick={() => postEditMut.mutate(postEditConfirm.amendments)}>この内容で修正する</Button>
          </div>
        ) : (
          <div className="flex justify-end gap-2"><Button variant="secondary" onClick={() => setPostEditConfirm(null)}>閉じる</Button></div>
        )}
      >
        {postEditConfirm && (postEditConfirm.result.blocked ? (
          <BlockedReasonMessage reason={postEditConfirm.result.block_reason ?? 'reduces_pay'} />
        ) : (
          <div className="space-y-2">
            <p className="text-xs text-text-sub">本人に通知されます。</p>
            {postEditConfirm.result.before && postEditConfirm.result.after && <SalaryDiffTable before={postEditConfirm.result.before} after={postEditConfirm.result.after} />}
          </div>
        ))}
      </Modal>
    </div>
  );
}

export function WorkRecordApprovalsPage() {
  const layoutTier = useLayoutTier();
  const [period, setPeriod] = useState(() => today().slice(0, 7));

  const { data: nodesData } = useQuery<NodesResponse>({ queryKey: ['nodes'], queryFn: () => apiFetch<NodesResponse>('/nodes') });
  const storeNodes = useMemo(() => flattenNodes(nodesData?.roots ?? [])
    .filter(({ node }: { node: NodeTreeItem }) => !node.children || node.children.length === 0), [nodesData]);
  // 追加セッション(2026-09-27、§13-29): 所属外店舗(応援)勤務の行に店舗名を表示するための
  // node_id→店舗名マップ (§7-5)。GET /approval-queue・GET /work-records のいずれも店舗名を
  // 返さないため、既に取得済みの /nodes から解決する (バックエンド変更不要)。
  const nodeNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const { node } of flattenNodes(nodesData?.roots ?? [])) map.set(node.node_id, node.name);
    return map;
  }, [nodesData]);
  const [nodeId, setNodeId] = useState('');

  const { data, isLoading } = useQuery<ApprovalQueueResponse>({
    queryKey: ['work-record-approval-queue', period, nodeId],
    queryFn: () => apiFetch<ApprovalQueueResponse>(`/work-records/approval-queue?period=${period}${nodeId ? `&node_id=${nodeId}` : ''}`),
  });
  const people = data?.people ?? [];
  const settings = data?.settings ?? { amend_on_approve: false, post_approval_edit_mode: 'locked' as const };

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  useEffect(() => { setSelectedKey(null); }, [period, nodeId]);
  const selected = people.find((p) => personKeyOf(p) === selectedKey) ?? null;
  const selectedForPanel = selected ?? (layoutTier === 'desktop' ? people[0] ?? null : null);

  const qc = useQueryClient();
  const onDecided = () => qc.invalidateQueries({ queryKey: ['work-record-approval-queue'] });

  const detail = selectedForPanel && (
    <PersonPeriodDetail key={personKeyOf(selectedForPanel)} person={selectedForPanel} period={period} settings={settings} onDecided={onDecided} nodeNameById={nodeNameById} />
  );

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-h1">勤務実績の承認</h1>
        <p className="text-sm text-text-sub mt-1">月次で提出された勤務実績を人ごとにまとめて確認・承認します。</p>
      </div>

      <div className="border border-surface-border rounded-lg bg-white p-4 space-y-3">
        <div className="flex flex-wrap gap-3">
          <div>
            <label className="block text-xs text-text-sub mb-1">対象年月</label>
            <MonthPicker value={period} onChange={setPeriod} aria-label="対象年月" className="border border-surface-border rounded px-3" />
          </div>
          {storeNodes.length > 1 && (
            <div className="w-56">
              <label className="block text-xs text-text-sub mb-1">店舗</label>
              <Select value={nodeId || undefined} onValueChange={setNodeId}>
                <SelectTrigger className="w-full min-h-[44px]"><SelectValue placeholder="すべての店舗" /></SelectTrigger>
                <SelectContent>
                  {storeNodes.map(({ node }: { node: NodeTreeItem }) => <SelectItem key={node.node_id} value={node.node_id}>{node.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {isLoading ? (
          <div className="text-text-sub text-sm">読み込み中…</div>
        ) : people.length === 0 ? (
          <EmptyState message="対象の実績はありません" description="対象年月・店舗を変えて確認してください" />
        ) : layoutTier === 'stack' ? (
          <div className="space-y-2">
            {people.map((p) => {
              const w = primaryWarning(p.warnings);
              const extra = warningCount(p.warnings) - (w ? 1 : 0);
              return (
                <button
                  key={personKeyOf(p)} type="button" onClick={() => setSelectedKey(personKeyOf(p))}
                  className="w-full min-h-[44px] flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg border border-surface-border bg-white text-left"
                >
                  <span className="min-w-0">
                    <span className="block font-medium text-text-body truncate">{p.display_name ?? '(氏名不明)'}</span>
                    <span className="block text-xs text-text-sub">{p.approvable_count > 0 ? `承認待ち ${p.approvable_count} 日分` : `確定済み ${p.approved_count} 日分`}</span>
                  </span>
                  <span className="flex items-center gap-1 shrink-0">
                    {p.approvable_count === 0 && <NeutralBadge>確定済み</NeutralBadge>}
                    {w && <WarningBadge warning={w} />}
                    {extra > 0 && <span className="text-xs text-text-sub">+{extra}</span>}
                  </span>
                </button>
              );
            })}
          </div>
        ) : (
          <div className={layoutTier === 'desktop' ? 'grid grid-cols-[minmax(0,1fr)_28rem] gap-4 items-start' : ''}>
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-text-sub border-b border-surface-border">
                  <th className="py-1.5 font-normal">氏名</th>
                  <th className="py-1.5 font-normal">状態</th>
                  <th className="py-1.5 font-normal">警告</th>
                </tr>
              </thead>
              <tbody>
                {people.map((p) => (
                  <tr
                    key={personKeyOf(p)}
                    tabIndex={0}
                    role="button"
                    aria-pressed={!!selectedForPanel && personKeyOf(selectedForPanel) === personKeyOf(p)}
                    onClick={() => setSelectedKey(personKeyOf(p))}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedKey(personKeyOf(p)); } }}
                    className={`cursor-pointer border-b border-surface-border hover:bg-surface-sunken focus-ring ${
                      selectedForPanel && personKeyOf(selectedForPanel) === personKeyOf(p) && layoutTier === 'desktop' ? 'bg-surface-sunken' : ''
                    }`}
                  >
                    <td className="py-2 pr-2 max-w-[12rem] truncate">{p.display_name ?? '(氏名不明)'}</td>
                    <td className="py-2 pr-2 tabular-nums whitespace-nowrap">
                      {p.approvable_count > 0 ? `承認待ち${p.approvable_count}日` : <NeutralBadge>確定済み{p.approved_count}日</NeutralBadge>}
                    </td>
                    <td className="py-2">
                      <span className="flex items-center gap-1 flex-wrap">
                        {p.warnings.minor_labor_violation || p.warnings.min_break_shortfall_days > 0
                          ? <WarningBadge warning={{ key: 'legal', label: '法令違反の疑い' }} /> : null}
                        {p.warnings.outside_home_node_work_days > 0
                          ? <WarningBadge warning={{ key: 'outside', label: `所属外店舗 ${p.warnings.outside_home_node_work_days}日` }} /> : null}
                        {p.warnings.proxy_entry_days > 0
                          ? <WarningBadge warning={{ key: 'proxy', label: `代理入力 ${p.warnings.proxy_entry_days}日` }} /> : null}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            {layoutTier === 'desktop' && (
              <div className="border border-surface-border rounded-lg p-4 sticky top-4">
                {detail ?? <p className="text-sm text-text-sub">氏名を選んでください。</p>}
              </div>
            )}
          </div>
        )}
      </div>

      {(layoutTier === 'stack' || layoutTier === 'tablet') && (
        <Modal
          open={!!selected} onClose={() => setSelectedKey(null)}
          title="月の明細" position={layoutTier === 'stack' ? 'bottom-sheet' : 'right'}
        >
          {detail}
        </Modal>
      )}
    </div>
  );
}
